import { useRef, useState } from "react";
import axios from "axios";
import { Download, Upload, Server, CheckCircle2, TriangleAlert, Database, Store, Printer, Settings2, WalletCards, Receipt, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { cacheBootstrapFromServer } from "@/db/offlineDb";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCES = [
  ["menu", "Menu", "Categories, groups, items, variations", Store],
  ["tables", "Tables", "Number, label, capacity, floor", Database],
  ["printers", "Printers", "Name, IP, port, paper width, copies", Printer],
  ["settings", "Settings", "Every restaurant_settings row", Settings2],
  ["payment_modes", "Payment modes", "Name, active, sort order", WalletCards],
  ["cancellation_reasons", "Cancellation reasons", "Label, applies to, active", Receipt],
  ["users", "Users", "Employee id, name, role (PIN is write-only)", UsersRound],
];

const describeError = error =>
  error?.response?.data?.detail || error?.message || "Request failed";

const formatFor = filename => (/\.csv$/i.test(filename) ? "csv" : /\.xlsx$/i.test(filename) ? "xlsx" : null);

const filenameFrom = (disposition, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(disposition || "");
  return match ? match[1] : fallback;
};

export default function SettingsPortability() {
  const { token } = usePOS();
  const inputs = useRef({});
  const [busy, setBusy] = useState("");
  const [results, setResults] = useState({});

  const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const report = (resource, result) => setResults(prev => ({ ...prev, [resource]: result }));

  const exportResource = async (resource, format) => {
    setBusy(`${resource}:export`);
    report(resource, null);
    try {
      const response = await axios.get(`${API}/admin/export/${resource}`, {
        ...auth,
        params: { format },
        responseType: "blob",
      });
      const filename = filenameFrom(response.headers?.["content-disposition"], `${resource}.${format}`);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      report(resource, { ok: true, message: `Downloaded ${filename} (${(response.data.size / 1024).toFixed(1)} KB)` });
      toast.success(`${filename} downloaded from the local server`);
    } catch (error) {
      const message = describeError(error);
      report(resource, { ok: false, message });
      toast.error(`Export failed — ${message}`);
    } finally {
      setBusy("");
    }
  };

  const pickFile = resource => inputs.current[resource]?.click();

  const importResource = async (resource, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const format = formatFor(file.name);
    if (!format) {
      const message = "Choose a .csv or .xlsx file — those are the two formats the server reads";
      report(resource, { ok: false, message });
      toast.error(message);
      return;
    }
    setBusy(`${resource}:import`);
    report(resource, null);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/admin/import/${resource}`, body, { ...auth, params: { format } });
      const errored = Array.isArray(data?.errored) ? data.errored : [];
      report(resource, {
        ok: errored.length === 0,
        message: `${data?.added ?? 0} added · ${data?.updated ?? 0} updated · ${data?.skipped ?? 0} unchanged · ${errored.length} rejected`,
        errored,
      });
      await cacheBootstrapFromServer(token);
      if (errored.length) toast.error(`${file.name}: ${errored.length} row(s) rejected — see the list`);
      else toast.success(`${file.name}: ${(data?.added ?? 0) + (data?.updated ?? 0)} row(s) written to the local server`);
    } catch (error) {
      const message = describeError(error);
      report(resource, { ok: false, message });
      toast.error(`Import failed — ${message}`);
    } finally {
      setBusy("");
    }
  };

  return <section className="bg-card border rounded-2xl shadow-sm flex flex-col h-full overflow-hidden" data-testid="settings-portability-panel">
    <header className="p-6 border-b flex items-start gap-4 bg-muted/20">
      <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Server className="w-5 h-5"/>
      </span>
      <div>
        <h3 className="text-lede font-semibold text-foreground">Master data export & import</h3>
        <p className="text-sm text-muted-foreground font-medium max-w-2xl mt-0.5">
          Reads and writes the local server's own tables. CSV and XLSX only — the two formats the import endpoint parses.
        </p>
      </div>
    </header>

    <div className="divide-y">
      {RESOURCES.map(([resource, label, detail, Icon]) => {
        const result = results[resource];
        const isBusy = busy.startsWith(`${resource}:`);
        
        return <div className="p-4 sm:p-5 flex flex-col gap-4 transition-colors hover:bg-muted/10" key={resource} data-testid={`portability-${resource}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-[200px]">
              <div className="w-8 h-8 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4"/>
              </div>
              <div className="flex flex-col">
                <strong className="text-body font-semibold text-foreground">{label}</strong>
                <small className="text-xs text-muted-foreground font-medium">{detail}</small>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" className="h-8 px-3 text-body font-medium" disabled={!!busy} onClick={() => exportResource(resource, "csv")} data-testid={`export-${resource}-csv-button`}>
                <Download className="w-3.5 h-3.5 mr-1.5"/>CSV
              </Button>
              <Button variant="outline" size="sm" className="h-8 px-3 text-body font-medium" disabled={!!busy} onClick={() => exportResource(resource, "xlsx")} data-testid={`export-${resource}-xlsx-button`}>
                <Download className="w-3.5 h-3.5 mr-1.5"/>XLSX
              </Button>
              <div className="w-px h-6 bg-border mx-1"></div>
              <Button variant="outline" size="sm" className="h-8 px-3 text-body font-medium" disabled={!!busy} onClick={() => pickFile(resource)} data-testid={`import-${resource}-button`}>
                <Upload className="w-3.5 h-3.5 mr-1.5"/>
                {busy === `${resource}:import` ? "Importing…" : "Import"}
              </Button>
            </div>
            
            <input
              className="hidden"
              type="file"
              accept=".csv,.xlsx"
              ref={element => { inputs.current[resource] = element; }}
              onChange={event => importResource(resource, event)}
              data-testid={`import-${resource}-input`}
            />
          </div>

          {result && (
            <div className={`p-3 rounded-xl border text-sm flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 ${result.ok ? "bg-success-subtle text-success border-success/40" : "bg-destructive/10 text-destructive border-destructive/20"}`} role={result.ok ? undefined : "alert"} data-testid={`portability-${resource}-result`}>
              <div className="flex items-start gap-2 font-medium">
                {result.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-success"/> : <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5"/>}
                <span>{result.message}</span>
              </div>
              
              {result.errored?.length > 0 && (
                <ul className="text-xs space-y-1 mt-1 ml-6 font-mono opacity-80" data-testid={`portability-${resource}-errors`}>
                  {result.errored.slice(0, 10).map(row => <li key={row.row}>Row {row.row}: {row.error}</li>)}
                  {result.errored.length > 10 && <li className="italic opacity-60">…and {result.errored.length - 10} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>;
      })}
    </div>
    
    <div className="p-4 bg-muted/30 border-t text-center text-xs text-muted-foreground font-medium" data-testid="settings-import-help">
      Export a sheet, edit it, import it back. Every row is applied by the server and counted in the result line above; nothing is reported as saved unless the server said so.
    </div>
  </section>;
}
